import { Space, Text } from "@/atoms"
import { ArrowRightOutlined } from "@/icons"
import { Heading } from "@/molecules/Heading"
import { Item, List } from "@/pages/DiscoveryPage/components/molecules/List"

import type { ApplicativeImageFinding, ImagePackageFix } from "@/domain/issue"
import type { BaseCardComponentProps } from "../../../RemediationGuide.types"

import sharedStyles from "../fixes-shared.module.scss"
import styles from "./DockerfileImageFixes.module.scss"
import {
  type DockerfileRebuildItem,
  getDockerfileRebuildImages,
  getDockerfileUpdateFixes,
  hasOsHierarchyFixes
} from "./utils"

export const DockerfileImageFixes: React.FC<
  BaseCardComponentProps & {
    imageFindings: ApplicativeImageFinding[]
    copyable?: boolean
  }
> = ({ imageFindings, copyable, t, tKey }) => {
  const updateFixes = getDockerfileUpdateFixes(imageFindings)
  const rebuildImages = getDockerfileRebuildImages(imageFindings)
  const totalCount = updateFixes.length + rebuildImages.length
  const suffixKey = hasOsHierarchyFixes(imageFindings)
    ? "updatePackage"
    : "updateImage"

  return (
    <Space orientation="vertical" className={styles.dockerfileSection}>
      <Heading
        level={6}
        title={`${t(`${tKey}${suffixKey}`, { count: totalCount })}:`}
      />
      <Text
        className={styles.sourceLabel}
      >{`${t(`${tKey}source`)}: Dockerfile`}</Text>
      {rebuildImages.length > 0 && (
        <List
          items={rebuildImages}
          render={({ imageName }: DockerfileRebuildItem) => {
            if (copyable) {
              return (
                <Item
                  key={imageName}
                  title={`${t("general.rebuildUpdate")}: ${imageName}`}
                />
              )
            }

            return (
              <Item
                wrap
                key={imageName}
                classes={{
                  item: sharedStyles.listItem,
                  value: sharedStyles.listItemValue
                }}
                title={
                  <Space size={4} baseline inline>
                    <Text className={sharedStyles.titleFixType}>
                      {t("general.rebuildUpdate")}:
                    </Text>
                    <Text className={sharedStyles.titleCurrentValue}>
                      {imageName}
                    </Text>
                  </Space>
                }
                value={""}
              />
            )
          }}
        />
      )}
      {updateFixes.length > 0 && (
        <List
          items={updateFixes}
          render={({
            packageName,
            fixType,
            currentVersion,
            version
          }: ImagePackageFix) => {
            if (copyable) {
              return (
                <Item
                  key={packageName + fixType}
                  title={`${t(`general.${fixType}Update`)}: ${packageName}${currentVersion ? ` :${currentVersion}` : ""}${version ? ` -> ${version}` : ""}`}
                />
              )
            }

            return (
              <Item
                wrap
                key={packageName + fixType}
                classes={{
                  item: sharedStyles.listItem,
                  value: sharedStyles.listItemValue
                }}
                title={
                  <Space inline size={4}>
                    <Space size={4} baseline inline>
                      <Text className={sharedStyles.titleFixType}>
                        {t(`general.${fixType}Update`)}:
                      </Text>
                      <Text className={sharedStyles.titleCurrentValue}>
                        {`${packageName}${currentVersion ? ` :${currentVersion}` : ""}`}
                      </Text>
                    </Space>
                    {version && (
                      <ArrowRightOutlined className={sharedStyles.titleArrow} />
                    )}
                  </Space>
                }
                value={version || ""}
              />
            )
          }}
        />
      )}
    </Space>
  )
}
